import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  alpha,
} from '@mui/material';
import {
  Phone,
  Web,
  Class,
  Event,
  AttachMoney,
  VerifiedUser,
  PersonOff,
  CheckCircle,
  Cancel,
  TrendingUp,
  CalendarToday,
  Email,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { apiService } from '../../services/apiService';
import { colors } from '../../theme/theme';
import { getAvatarColor, getUserInitials } from '../../utils/userHelpers';
import { Lead, BookingLeadFilter } from './types';
import {
  StatCard,
  SearchBar,
  FilterPills,
  DATA_GRID_SX,
  filterCardSx,
  tableCardSx,
  STAT_CARDS_GRID_SX,
  DATA_GRID_CONTAINER_SX,
  isThisMonth,
} from './shared';

const BOOKING_FILTERS: { value: BookingLeadFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'booked', label: 'Booked' },
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'unverified', label: 'Unverified' },
];

function isLeadVerified(lead: Lead): boolean {
  return lead.source === 'mobile_app' ? true : lead.isAuthenticated;
}

export function BookingLeadsTab() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<BookingLeadFilter>('all');

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getLeads();
      const leadsData: Lead[] = response.leads || [];
      leadsData.sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt).getTime();
        const dateB = new Date(b.updatedAt || b.createdAt).getTime();
        return dateB - dateA;
      });
      setLeads(leadsData);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch leads');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredLeads = useMemo(() => {
    const searchLower = searchTerm.toLowerCase().trim();

    return leads.filter((lead) => {
      const studioName =
        lead.session?.studio?.studioName ||
        lead.event?.studio?.studioName ||
        lead.booking?.studio?.studioName ||
        '';
      const userName = lead.user?.name || lead.booking?.user?.name || '';
      const userEmail = lead.user?.email || lead.booking?.user?.email || '';

      const matchesSearch =
        searchLower === '' ||
        lead.phoneNumber?.toLowerCase().includes(searchLower) ||
        userName.toLowerCase().includes(searchLower) ||
        userEmail.toLowerCase().includes(searchLower) ||
        lead.className?.toLowerCase().includes(searchLower) ||
        lead.eventName?.toLowerCase().includes(searchLower) ||
        lead.session?.className?.toLowerCase().includes(searchLower) ||
        lead.event?.eventName?.toLowerCase().includes(searchLower) ||
        studioName.toLowerCase().includes(searchLower) ||
        lead.source?.toLowerCase().includes(searchLower);

      const verified = isLeadVerified(lead);
      const matchesFilter =
        statusFilter === 'all' ||
        (statusFilter === 'booked' && lead.bookingCompleted) ||
        (statusFilter === 'pending' && !lead.bookingCompleted) ||
        (statusFilter === 'verified' && verified) ||
        (statusFilter === 'unverified' && !verified);

      return matchesSearch && matchesFilter;
    });
  }, [leads, searchTerm, statusFilter]);

  const columns: GridColDef[] = [
    {
      field: 'user',
      headerName: 'User Details',
      flex: 1,
      minWidth: 280,
      renderCell: (params) => {
        const lead = params.row as Lead;
        const user = lead.user || lead.booking?.user;
        const phoneNumber = lead.phoneNumber;
        const displayName = user?.name || phoneNumber || 'Unknown';
        const initials = getUserInitials(displayName);
        const avatarColor = getAvatarColor(displayName);

        return (
          <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: alpha(avatarColor, 0.15),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Typography variant="body2" fontWeight={700} sx={{ color: avatarColor }}>
                {initials}
              </Typography>
            </Box>
            <Box minWidth={0}>
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: colors.textPrimary,
                  fontWeight: 600,
                }}
              >
                {user?.name || 'N/A'}
              </Typography>
              {phoneNumber && (
                <Typography
                  variant="caption"
                  color={colors.textSecondary}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}
                >
                  <Phone sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                  {phoneNumber}
                </Typography>
              )}
              {user?.email && (
                <Typography
                  variant="caption"
                  color={colors.textSecondary}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}
                >
                  <Email sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                  {user.email}
                </Typography>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ py: 1 }}>
          <Chip
            label={params.value === 'website' ? 'Website' : 'Mobile App'}
            size="small"
            icon={<Web sx={{ fontSize: 14 }} />}
            sx={{
              backgroundColor: alpha(
                params.value === 'website' ? colors.info : colors.warning,
                0.1
              ),
              color: params.value === 'website' ? colors.info : colors.warning,
              border: `1px solid ${alpha(
                params.value === 'website' ? colors.info : colors.warning,
                0.3
              )}`,
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 28,
              '& .MuiChip-icon': {
                color: params.value === 'website' ? colors.info : colors.warning,
              },
            }}
          />
        </Box>
      ),
    },
    {
      field: 'studio',
      headerName: 'Studio',
      width: 180,
      renderCell: (params) => {
        const lead = params.row as Lead;
        const studio = lead.session?.studio || lead.event?.studio || lead.booking?.studio;

        return (
          <Box sx={{ py: 1 }}>
            {studio ? (
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: colors.textPrimary,
                    fontWeight: 600,
                  }}
                >
                  {studio.studioName}
                </Typography>
                {studio.city && (
                  <Typography
                    variant="caption"
                    color={colors.textSecondary}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                    }}
                  >
                    {studio.city}
                    {studio.state ? `, ${studio.state}` : ''}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color={colors.textSecondary} fontStyle="italic">
                N/A
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'className',
      headerName: 'Class/Event',
      width: 200,
      renderCell: (params) => {
        const lead = params.row as Lead;
        const itemName =
          lead.className ||
          lead.eventName ||
          lead.session?.className ||
          lead.event?.eventName ||
          'N/A';
        const isEvent = !!lead.eventName || !!lead.event;
        const duration = lead.session?.duration;

        return (
          <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
            {isEvent ? (
              <Event sx={{ fontSize: 18, color: colors.warning }} />
            ) : (
              <Class sx={{ fontSize: 18, color: colors.primary }} />
            )}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: colors.textPrimary,
                  fontWeight: 500,
                }}
              >
                {itemName}
              </Typography>
              {duration && (
                <Typography variant="caption" color={colors.textSecondary} display="block">
                  {duration} min
                </Typography>
              )}
              {lead.session?.slots !== undefined && (
                <Typography variant="caption" color={colors.textSecondary} display="block">
                  {lead.session.slots} slots
                </Typography>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'date',
      headerName: 'Date & Time',
      width: 180,
      renderCell: (params) => {
        const lead = params.row as Lead;
        const date = lead.session?.date || lead.event?.date;

        return (
          <Box sx={{ py: 1 }}>
            {date ? (
              <Box>
                <Typography variant="body2" fontWeight="bold" color={colors.textPrimary}>
                  {new Date(date).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </Typography>
                <Typography variant="caption" color={colors.textSecondary} display="block">
                  {new Date(date).toLocaleTimeString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color={colors.textSecondary} fontStyle="italic">
                N/A
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 120,
      renderCell: (params) => {
        const lead = params.row as Lead;
        const price = params.value || lead.session?.price || lead.event?.price || 0;
        return (
          <Box display="flex" alignItems="center" gap={1} sx={{ py: 1 }}>
            <AttachMoney sx={{ fontSize: 18, color: colors.success }} />
            <Typography variant="body2" color={colors.textPrimary} fontWeight="600">
              ₹{price.toLocaleString('en-IN')}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'isAuthenticated',
      headerName: 'Auth',
      width: 140,
      renderCell: (params) => {
        const lead = params.row as Lead;
        const isAuthenticated = isLeadVerified(lead);
        return (
          <Box sx={{ py: 1 }}>
            <Chip
              label={isAuthenticated ? 'Verified' : 'Not Verified'}
              size="small"
              icon={
                isAuthenticated ? (
                  <VerifiedUser sx={{ fontSize: 14 }} />
                ) : (
                  <PersonOff sx={{ fontSize: 14 }} />
                )
              }
              sx={{
                backgroundColor: alpha(isAuthenticated ? colors.success : colors.error, 0.1),
                color: isAuthenticated ? colors.success : colors.error,
                border: `1px solid ${alpha(isAuthenticated ? colors.success : colors.error, 0.3)}`,
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 28,
                '& .MuiChip-icon': {
                  color: isAuthenticated ? colors.success : colors.error,
                },
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'bookingCompleted',
      headerName: 'Booking',
      width: 140,
      renderCell: (params) => (
        <Box sx={{ py: 1 }}>
          <Chip
            label={params.value ? 'Booked' : 'Pending'}
            size="small"
            icon={
              params.value ? (
                <CheckCircle sx={{ fontSize: 14 }} />
              ) : (
                <Cancel sx={{ fontSize: 14 }} />
              )
            }
            sx={{
              backgroundColor: alpha(params.value ? colors.success : colors.warning, 0.1),
              color: params.value ? colors.success : colors.warning,
              border: `1px solid ${alpha(params.value ? colors.success : colors.warning, 0.3)}`,
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 28,
              '& .MuiChip-icon': {
                color: params.value ? colors.success : colors.warning,
              },
            }}
          />
        </Box>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const totalLeads = leads.length;
  const completedLeads = leads.filter((l) => l.bookingCompleted).length;
  const pendingLeads = totalLeads - completedLeads;
  const verifiedLeads = leads.filter((l) => isLeadVerified(l)).length;
  const bookedPct = totalLeads > 0 ? Math.round((completedLeads / totalLeads) * 100) : 0;
  const verifiedPct = totalLeads > 0 ? Math.round((verifiedLeads / totalLeads) * 100) : 0;
  const leadsThisMonth = leads.filter((l) => isThisMonth(l.createdAt)).length;

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={STAT_CARDS_GRID_SX}>
        <StatCard
          label="Total Leads"
          value={totalLeads}
          icon={<TrendingUp sx={{ fontSize: 28, color: colors.primary }} />}
          color={colors.primary}
        />
        <StatCard
          label="Booked"
          value={completedLeads}
          subtitle={`${bookedPct}% of total`}
          icon={<CheckCircle sx={{ fontSize: 28, color: colors.success }} />}
          color={colors.success}
        />
        <StatCard
          label="Pending Booking"
          value={pendingLeads}
          subtitle="awaiting"
          icon={<Cancel sx={{ fontSize: 28, color: colors.warning }} />}
          color={colors.warning}
        />
        <StatCard
          label="Verified"
          value={verifiedLeads}
          subtitle={`${verifiedPct}% of total`}
          icon={<VerifiedUser sx={{ fontSize: 28, color: colors.success }} />}
          color={colors.success}
        />
        <StatCard
          label="This Month"
          value={leadsThisMonth}
          subtitle="new leads"
          icon={<CalendarToday sx={{ fontSize: 28, color: colors.info }} />}
          color={colors.info}
        />
      </Box>

      <SearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search by name, phone, email, studio, class..."
      />

      <Card sx={filterCardSx}>
        <CardContent sx={{ py: 2.5, px: 3 }}>
          <FilterPills options={BOOKING_FILTERS} value={statusFilter} onChange={setStatusFilter} />
        </CardContent>
      </Card>

      <Card sx={tableCardSx}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={DATA_GRID_CONTAINER_SX}>
            <DataGrid
              rows={filteredLeads}
              columns={columns}
              getRowId={(row) => row._id}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              getRowHeight={() => 75}
              sx={DATA_GRID_SX}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
